import { API_BASE_URL } from '../config';
import React, { useState, useEffect } from 'react';

const card = { padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' };
const inputStyle = { width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' };

function rupee(value) {
  return `₹${(Number(value) || 0).toFixed(2)}`;
}

function todayInput() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function monthStartInput() {
  const today = todayInput();
  return `${today.slice(0, 8)}01`;
}

function isoWeekOf(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const weekday = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - weekday);
  const isoYear = date.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const week = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return { year: isoYear, week };
}

function mondayOfWeek(year, week) {
  const jan4 = new Date(Date.UTC(Number(year), 0, 4));
  const weekday = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - (weekday - 1) + (Number(week) - 1) * 7);
  return monday.toISOString().slice(0, 10);
}

function sundayOfWeek(year, week) {
  const sunday = new Date(`${mondayOfWeek(year, week)}T00:00:00Z`);
  sunday.setUTCDate(sunday.getUTCDate() + 6);
  return sunday.toISOString().slice(0, 10);
}

function monthEndDate(monthValue) {
  const [year, month] = monthValue.split('-').map(Number);
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${monthValue}-${String(last).padStart(2, '0')}`;
}

function defaultSummaryRange(frequency, today = todayInput()) {
  const week = isoWeekOf(today);
  const month = today.slice(0, 7);
  const year = Number(today.slice(0, 4));
  if (frequency === 'weekly') {
    return {
      start: mondayOfWeek(week.year, week.week),
      end: sundayOfWeek(week.year, week.week),
      weekFrom: week,
      weekTo: week
    };
  }
  if (frequency === 'monthly') {
    return { start: `${month}-01`, end: monthEndDate(month), monthFrom: month, monthTo: month };
  }
  if (frequency === 'yearly') {
    return { start: `${year}-01-01`, end: `${year}-12-31`, yearFrom: year, yearTo: year };
  }
  return { start: today, end: today, weekFrom: week, weekTo: week, monthFrom: month, monthTo: month, yearFrom: year, yearTo: year };
}

const FinanceDashboard = () => {
  const [activeSection, setActiveSection] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [startDate, setStartDate] = useState(monthStartInput());
  const [endDate, setEndDate] = useState(todayInput());
  const [salesReport, setSalesReport] = useState(null);
  const [profitReport, setProfitReport] = useState(null);
  const [taxReport, setTaxReport] = useState(null);
  const [taxRates, setTaxRates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [frequency, setFrequency] = useState('daily');
  const [summaryStart, setSummaryStart] = useState(todayInput());
  const [summaryEnd, setSummaryEnd] = useState(todayInput());
  const openingWeek = isoWeekOf(todayInput());
  const [weekFromYear, setWeekFromYear] = useState(openingWeek.year);
  const [weekFrom, setWeekFrom] = useState(openingWeek.week);
  const [weekToYear, setWeekToYear] = useState(openingWeek.year);
  const [weekTo, setWeekTo] = useState(openingWeek.week);
  const [monthFrom, setMonthFrom] = useState(todayInput().slice(0, 7));
  const [monthTo, setMonthTo] = useState(todayInput().slice(0, 7));
  const [yearFrom, setYearFrom] = useState(Number(todayInput().slice(0, 4)));
  const [yearTo, setYearTo] = useState(Number(todayInput().slice(0, 4)));
  const [chartType, setChartType] = useState('bar');
  const [showLabels, setShowLabels] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [seriesOn, setSeriesOn] = useState({ revenue: true, tax: true, purchases: true, profit: true, invoiceAmount: true });
  const [financeSummary, setFinanceSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [taxForm, setTaxForm] = useState({
    TAX_NAME: '',
    TAX_TYPE: 'GST',
    TAX_RATE: '',
    APPLICABLE_TO: 'All',
    EFFECTIVE_FROM: todayInput()
  });

  const hasPermission = (gadgetName, permissionType = 'VIEW') => {
    const permission = userPermissions[gadgetName];
    return permission && permission[permissionType] === true;
  };

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  };

  useEffect(() => {
    const headers = authHeaders();
    if (!headers) {
      setPermissionsLoading(false);
      return;
    }
    fetch(`${API_BASE_URL}/gadgetPermissions/my-permissions`, { headers })
      .then((response) => response.ok ? response.json() : {})
      .then((permissions) => setUserPermissions(permissions || {}))
      .catch(() => setUserPermissions({}))
      .finally(() => setPermissionsLoading(false));
  }, []);

  const loadReport = async (section, overrides = {}) => {
    const headers = authHeaders();
    if (!headers) return;
    setLoading(true);
    setError('');
    setNotice('');
    const rangeStart = overrides.startDate || startDate;
    const rangeEnd = overrides.endDate || endDate;
    const nextFrequency = overrides.frequency || frequency;
    const range = `startDate=${rangeStart}&endDate=${rangeEnd}`;
    try {
      if (section === 'summary') {
        const params = new URLSearchParams({
          frequency: nextFrequency,
          startDate: overrides.startDate || summaryStart,
          endDate: overrides.endDate || summaryEnd
        });
        const response = await fetch(`${API_BASE_URL}/finance/summary?${params}`, { headers });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Could not load the finance summary.');
        setFinanceSummary(data);
      }
      if (section === 'sales') {
        const response = await fetch(`${API_BASE_URL}/sales/analytics/summary?${range}`, { headers });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Could not load the sales report.');
        setSalesReport(data);
      }
      if (section === 'profit') {
        const response = await fetch(`${API_BASE_URL}/medicinePurchases/analytics/profit-margin?${range}`, { headers });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Could not load the profit report.');
        setProfitReport(data);
      }
      if (section === 'tax') {
        const [reportResponse, ratesResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/tax/report?${range}`, { headers }),
          fetch(`${API_BASE_URL}/tax/config`, { headers })
        ]);
        const report = await reportResponse.json();
        const rates = await ratesResponse.json();
        if (!reportResponse.ok) throw new Error(report.message || 'Could not load the tax report.');
        setTaxReport(report);
        setTaxRates(Array.isArray(rates) ? rates : []);
      }
      if (section === 'invoices') {
        const response = await fetch(`${API_BASE_URL}/invoices?${range}&limit=50`, { headers });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Could not load invoices.');
        setInvoices(Array.isArray(data.invoices) ? data.invoices : []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openSection = (section) => {
    const next = activeSection === section ? null : section;
    setActiveSection(next);
    setError('');
    setNotice('');
    if (next) loadReport(next);
  };

  const saveTaxRate = async () => {
    const headers = authHeaders();
    if (!headers) return;
    setError('');
    setNotice('');
    const response = await fetch(`${API_BASE_URL}/tax/config`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...taxForm,
        TAX_RATE: Number(taxForm.TAX_RATE),
        APPLICABLE_TO: [taxForm.APPLICABLE_TO]
      })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(data.message || 'Could not save the tax rate.');
      return;
    }
    setNotice('Tax rate saved.');
    setTaxForm({ TAX_NAME: '', TAX_TYPE: 'GST', TAX_RATE: '', APPLICABLE_TO: 'All', EFFECTIVE_FROM: todayInput() });
    loadReport('tax');
  };

  const setWeekFields = (from, to) => {
    setWeekFromYear(from.year);
    setWeekFrom(from.week);
    setWeekToYear(to.year);
    setWeekTo(to.week);
  };

  const changeSummaryDate = (which, value) => {
    const start = which === 'start' ? value : summaryStart;
    const end = which === 'end' ? value : summaryEnd;
    setSummaryStart(start);
    setSummaryEnd(end);
    if (start && end && start <= end) setWeekFields(isoWeekOf(start), isoWeekOf(end));
  };

  const changeWeek = (which, field, raw) => {
    const value = Math.max(field === 'week' ? 1 : 2000, Math.min(field === 'week' ? 53 : 2100, Number(raw) || 0));
    const next = { fromYear: weekFromYear, fromWeek: weekFrom, toYear: weekToYear, toWeek: weekTo };
    if (which === 'from' && field === 'year') next.fromYear = value;
    if (which === 'from' && field === 'week') next.fromWeek = value;
    if (which === 'to' && field === 'year') next.toYear = value;
    if (which === 'to' && field === 'week') next.toWeek = value;
    setWeekFromYear(next.fromYear);
    setWeekFrom(next.fromWeek);
    setWeekToYear(next.toYear);
    setWeekTo(next.toWeek);
    setSummaryStart(mondayOfWeek(next.fromYear, next.fromWeek));
    setSummaryEnd(sundayOfWeek(next.toYear, next.toWeek));
  };

  const chooseFrequency = (next) => {
    const range = defaultSummaryRange(next);
    setFrequency(next);
    setSummaryStart(range.start);
    setSummaryEnd(range.end);
    if (range.weekFrom) setWeekFields(range.weekFrom, range.weekTo);
    if (range.monthFrom) {
      setMonthFrom(range.monthFrom);
      setMonthTo(range.monthTo);
    }
    if (range.yearFrom) {
      setYearFrom(range.yearFrom);
      setYearTo(range.yearTo);
    }
    loadReport('summary', { frequency: next, startDate: range.start, endDate: range.end });
  };

  const applySummaryRange = () => {
    let start = summaryStart;
    let end = summaryEnd;
    if (frequency === 'weekly') {
      start = mondayOfWeek(weekFromYear, weekFrom);
      end = sundayOfWeek(weekToYear, weekTo);
    }
    if (frequency === 'monthly') {
      const from = monthFrom <= monthTo ? monthFrom : monthTo;
      const to = monthFrom <= monthTo ? monthTo : monthFrom;
      start = `${from}-01`;
      end = monthEndDate(to);
      setMonthFrom(from);
      setMonthTo(to);
    }
    if (frequency === 'yearly') {
      const from = Math.min(Number(yearFrom), Number(yearTo));
      const to = Math.max(Number(yearFrom), Number(yearTo));
      start = `${from}-01-01`;
      end = `${to}-12-31`;
      setYearFrom(from);
      setYearTo(to);
    }
    if (start > end) {
      const swap = start;
      start = end;
      end = swap;
    }
    setSummaryStart(start);
    setSummaryEnd(end);
    if (frequency === 'weekly') setWeekFields(isoWeekOf(start), isoWeekOf(end));
    loadReport('summary', { frequency, startDate: start, endDate: end });
  };

  const gadgets = [
    hasPermission('Finance Summary'),
    hasPermission('Sales Analytics'),
    hasPermission('Profit Analysis'),
    hasPermission('Tax Reporting'),
    hasPermission('Invoice Generation')
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '2rem auto', padding: '2rem' }}>
      <h2>Finance Dashboard</h2>
      {permissionsLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>Loading dashboard permissions...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {hasPermission('Finance Summary') && (
            <div style={card}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>Summary</h3>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>Revenue, tax, purchases, profit, and bills by day, week, month, year, or a custom period.</p>
              <button type="button" onClick={() => openSection('summary')} style={{ width: '100%', padding: '10px', backgroundColor: activeSection === 'summary' ? '#dc3545' : '#0f766e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                {activeSection === 'summary' ? 'Close' : 'Open summary'}
              </button>
            </div>
          )}
          {hasPermission('Sales Analytics') && (
            <div style={card}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>Sales report</h3>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>Revenue, tax, discounts, and sales by type.</p>
              <button type="button" onClick={() => openSection('sales')} style={{ width: '100%', padding: '10px', backgroundColor: activeSection === 'sales' ? '#dc3545' : '#198754', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                {activeSection === 'sales' ? 'Close' : 'Open report'}
              </button>
            </div>
          )}
          {hasPermission('Profit Analysis') && (
            <div style={card}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>Profit report</h3>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>Medicine sales against purchases for the selected dates.</p>
              <button type="button" onClick={() => openSection('profit')} style={{ width: '100%', padding: '10px', backgroundColor: activeSection === 'profit' ? '#dc3545' : '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                {activeSection === 'profit' ? 'Close' : 'Open report'}
              </button>
            </div>
          )}
          {hasPermission('Tax Reporting') && (
            <div style={card}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>Tax</h3>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>Tax collected on sales and invoices, and the rates in use.</p>
              <button type="button" onClick={() => openSection('tax')} style={{ width: '100%', padding: '10px', backgroundColor: activeSection === 'tax' ? '#dc3545' : '#fd7e14', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                {activeSection === 'tax' ? 'Close' : 'Open tax'}
              </button>
            </div>
          )}
          {hasPermission('Invoice Generation') && (
            <div style={card}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>Invoice register</h3>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#6c757d' }}>Bills raised in the selected period, with tax and total.</p>
              <button type="button" onClick={() => openSection('invoices')} style={{ width: '100%', padding: '10px', backgroundColor: activeSection === 'invoices' ? '#dc3545' : '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                {activeSection === 'invoices' ? 'Close' : 'Open register'}
              </button>
            </div>
          )}
          {!permissionsLoading && gadgets.every((allowed) => !allowed) && (
            <p style={{ color: '#6c757d' }}>No finance gadgets are assigned to this login.</p>
          )}
        </div>
      )}

      {activeSection && (
        <div style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px' }}>
          {activeSection === 'summary' ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'end', marginBottom: '1rem' }}>
              {['daily', 'weekly', 'monthly', 'yearly', 'custom'].map((option) => (
                <button key={option} type="button" onClick={() => chooseFrequency(option)} style={{ padding: '8px 14px', borderRadius: '4px', border: '1px solid #0f766e', cursor: 'pointer', backgroundColor: frequency === option ? '#0f766e' : 'white', color: frequency === option ? 'white' : '#0f766e', textTransform: 'capitalize' }}>
                  {option}
                </button>
              ))}
              {frequency === 'weekly' && (
                <>
                  <label>Start week
                    <span style={{ display: 'flex', gap: '0.35rem' }}>
                      <input aria-label="Start week year" type="number" min="2000" max="2100" value={weekFromYear} onChange={(event) => changeWeek('from', 'year', event.target.value)} style={{ ...inputStyle, width: 88 }} />
                      <input aria-label="Start week number" type="number" min="1" max="53" value={weekFrom} onChange={(event) => changeWeek('from', 'week', event.target.value)} style={{ ...inputStyle, width: 72 }} />
                    </span>
                  </label>
                  <label>End week
                    <span style={{ display: 'flex', gap: '0.35rem' }}>
                      <input aria-label="End week year" type="number" min="2000" max="2100" value={weekToYear} onChange={(event) => changeWeek('to', 'year', event.target.value)} style={{ ...inputStyle, width: 88 }} />
                      <input aria-label="End week number" type="number" min="1" max="53" value={weekTo} onChange={(event) => changeWeek('to', 'week', event.target.value)} style={{ ...inputStyle, width: 72 }} />
                    </span>
                  </label>
                </>
              )}
              {(frequency === 'daily' || frequency === 'weekly' || frequency === 'custom') && (
                <>
                  <label>From
                    <input type="date" value={summaryStart} onChange={(event) => changeSummaryDate('start', event.target.value)} style={{ ...inputStyle, width: 160 }} />
                  </label>
                  <label>To
                    <input type="date" value={summaryEnd} onChange={(event) => changeSummaryDate('end', event.target.value)} style={{ ...inputStyle, width: 160 }} />
                  </label>
                </>
              )}
              {frequency === 'monthly' && (
                <>
                  <label>From
                    <input type="month" value={monthFrom} onChange={(event) => setMonthFrom(event.target.value)} style={{ ...inputStyle, width: 160 }} />
                  </label>
                  <label>To
                    <input type="month" value={monthTo} onChange={(event) => setMonthTo(event.target.value)} style={{ ...inputStyle, width: 160 }} />
                  </label>
                </>
              )}
              {frequency === 'yearly' && (
                <>
                  <label>From
                    <input aria-label="From year" type="number" min="2000" max="2100" value={yearFrom} onChange={(event) => setYearFrom(event.target.value)} style={{ ...inputStyle, width: 100 }} />
                  </label>
                  <label>To
                    <input aria-label="To year" type="number" min="2000" max="2100" value={yearTo} onChange={(event) => setYearTo(event.target.value)} style={{ ...inputStyle, width: 100 }} />
                  </label>
                </>
              )}
              <button type="button" onClick={applySummaryRange} disabled={loading} style={{ padding: '10px 16px', backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                {loading ? 'Loading...' : 'Apply'}
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '1rem', alignItems: 'end', marginBottom: '1rem' }}>
              <label>From
                <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} style={inputStyle} />
              </label>
              <label>To
                <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} style={inputStyle} />
              </label>
              <button type="button" onClick={() => loadReport(activeSection)} disabled={loading} style={{ padding: '10px 16px', backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          )}
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {notice && <p style={{ color: '#155724' }}>{notice}</p>}

          {activeSection === 'summary' && financeSummary && (
            <div>
              <FinanceChart
                buckets={financeSummary.buckets || []}
                grain={financeSummary.grain}
                chartType={chartType}
                onChartType={setChartType}
                showLabels={showLabels}
                onLabels={setShowLabels}
                showGrid={showGrid}
                onGrid={setShowGrid}
                seriesOn={seriesOn}
                onToggleSeries={(key) => setSeriesOn((current) => ({ ...current, [key]: !current[key] }))}
              />
              <p style={{ margin: '1rem 0 0', color: '#6c757d', fontSize: '0.9rem' }}>
                Profit is revenue minus medicine purchases. Bill amount lists invoices raised in the period and is not added to revenue.
              </p>
              <SimpleTable
                headers={['Period', 'Revenue', 'Tax', 'Purchases', 'Profit', 'Sales', 'Bills', 'Bill amount']}
                rows={[
                  ['Total', rupee(financeSummary.totals.revenue), rupee(financeSummary.totals.tax), rupee(financeSummary.totals.purchases), rupee(financeSummary.totals.profit), financeSummary.totals.sales, financeSummary.totals.invoices, rupee(financeSummary.totals.invoiceAmount)],
                  ...(financeSummary.buckets || []).map((row) => [row.label, rupee(row.revenue), rupee(row.tax), rupee(row.purchases), rupee(row.profit), row.sales, row.invoices, rupee(row.invoiceAmount)])
                ]}
              />
            </div>
          )}

          {activeSection === 'sales' && salesReport && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
                <Summary label="Sales" value={salesReport.summary.totalSales} />
                <Summary label="Revenue" value={rupee(salesReport.summary.totalRevenue)} />
                <Summary label="Tax" value={rupee(salesReport.summary.totalTax)} />
                <Summary label="Discounts" value={rupee(salesReport.summary.totalDiscount)} />
                <Summary label="Average sale" value={rupee(salesReport.summary.averageSaleAmount)} />
              </div>
              <SimpleTable
                headers={['Type', 'Count', 'Amount']}
                rows={(salesReport.salesByType || []).map((row) => [row._id || 'Unspecified', row.count, rupee(row.totalAmount)])}
              />
            </div>
          )}

          {activeSection === 'profit' && profitReport && profitReport.summary && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
                <Summary label="Sales" value={rupee(profitReport.summary.totalSales)} />
                <Summary label="Purchases" value={rupee(profitReport.summary.totalPurchases)} />
                <Summary label="Profit" value={rupee(profitReport.summary.totalProfit)} />
                <Summary label="Margin" value={`${(Number(profitReport.summary.overallProfitMargin) || 0).toFixed(2)}%`} />
              </div>
              <SimpleTable
                headers={['Medicine', 'Sales', 'Purchases', 'Profit', 'Margin']}
                rows={(profitReport.details || []).map((item) => [
                  item.medicineName || item.medicineId,
                  rupee(item.totalSales),
                  rupee(item.totalPurchases),
                  rupee(item.profit),
                  `${(Number(item.profitMargin) || 0).toFixed(2)}%`
                ])}
              />
            </div>
          )}

          {activeSection === 'tax' && taxReport && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
                <Summary label="Sales tax" value={rupee(taxReport.summary.totalSalesTax)} />
                <Summary label="Invoice tax" value={rupee(taxReport.summary.totalInvoiceTax)} />
                <Summary label="Total tax" value={rupee(taxReport.summary.totalTax)} />
                <Summary label="Invoices" value={taxReport.summary.totalInvoiceCount} />
              </div>
              <h4>Rates</h4>
              <SimpleTable
                headers={['Name', 'Type', 'Rate', 'Applies to', 'Active']}
                rows={taxRates.map((rate) => [rate.TAX_NAME, rate.TAX_TYPE, `${rate.TAX_RATE}%`, (rate.APPLICABLE_TO || []).join(', '), rate.IS_ACTIVE ? 'Yes' : 'No'])}
              />
              {hasPermission('Tax Reporting', 'CREATE') && (
                <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
                  <input placeholder="Tax name" value={taxForm.TAX_NAME} onChange={(event) => setTaxForm({ ...taxForm, TAX_NAME: event.target.value })} style={inputStyle} />
                  <select value={taxForm.TAX_TYPE} onChange={(event) => setTaxForm({ ...taxForm, TAX_TYPE: event.target.value })} style={inputStyle}>
                    <option>GST</option>
                    <option>VAT</option>
                    <option>Service Tax</option>
                    <option>Custom Tax</option>
                    <option>Other</option>
                  </select>
                  <input type="number" min="0" max="100" placeholder="Rate %" value={taxForm.TAX_RATE} onChange={(event) => setTaxForm({ ...taxForm, TAX_RATE: event.target.value })} style={inputStyle} />
                  <select value={taxForm.APPLICABLE_TO} onChange={(event) => setTaxForm({ ...taxForm, APPLICABLE_TO: event.target.value })} style={inputStyle}>
                    <option>All</option>
                    <option>Medicine</option>
                    <option>Consultation</option>
                    <option>LabTest</option>
                    <option>Procedure</option>
                  </select>
                  <input type="date" value={taxForm.EFFECTIVE_FROM} onChange={(event) => setTaxForm({ ...taxForm, EFFECTIVE_FROM: event.target.value })} style={inputStyle} />
                  <button type="button" onClick={saveTaxRate} style={{ padding: '8px', backgroundColor: '#fd7e14', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save rate</button>
                </div>
              )}
            </div>
          )}

          {activeSection === 'invoices' && (
            <SimpleTable
              headers={['Invoice', 'Patient', 'Date', 'Status', 'Tax', 'Total']}
              rows={invoices.map((invoice) => [
                invoice.INVOICE_ID || invoice._id,
                invoice.PATIENT_ID?.PATIENT_NAME || '',
                invoice.INVOICE_DATE ? new Date(invoice.INVOICE_DATE).toLocaleDateString() : '',
                invoice.INVOICE_STATUS || '',
                rupee(invoice.TAX_AMOUNT),
                rupee(invoice.TOTAL_AMOUNT)
              ])}
            />
          )}
        </div>
      )}
    </div>
  );
};

function Summary({ label, value }) {
  return (
    <div style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
      <div style={{ color: '#6c757d', fontSize: '0.85rem' }}>{label}</div>
      <strong>{value}</strong>
    </div>
  );
}

const CHART_SERIES = [
  { key: 'revenue', label: 'Revenue', color: '#1f4d40' },
  { key: 'tax', label: 'Tax', color: '#c47b12' },
  { key: 'purchases', label: 'Purchases', color: '#1565c0' },
  { key: 'profit', label: 'Profit', color: '#2e7d32' },
  { key: 'invoiceAmount', label: 'Bill amount', color: '#6f42c1' }
];

function chartLabel(bucket, grain) {
  if (grain === 'week') return (bucket.key || '').split('-')[1] || bucket.label;
  if (grain === 'year') return bucket.key;
  if (grain === 'month') {
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const [year, month] = (bucket.key || '').split('-');
    return `${names[Number(month) - 1] || bucket.label} ${String(year || '').slice(2)}`;
  }
  return bucket.label;
}

function shortAmount(value) {
  const amount = Number(value) || 0;
  const sign = amount < 0 ? '-' : '';
  const abs = Math.abs(amount);
  if (abs >= 100000) return `${sign}${(abs / 100000).toFixed(1)}L`;
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
  return `${sign}${Math.round(abs)}`;
}

function FinanceChart({ buckets, grain, chartType, onChartType, showLabels, onLabels, showGrid, onGrid, seriesOn, onToggleSeries }) {
  const active = CHART_SERIES.filter((item) => seriesOn[item.key]);
  const max = Math.max(1, ...buckets.flatMap((bucket) => active.map((item) => Math.abs(Number(bucket[item.key]) || 0))));
  const barWidth = 8;
  const groupWidth = chartType === 'line' ? 64 : Math.max(active.length * barWidth + 18, 40);
  const left = showGrid ? 46 : 12;
  const plot = 100;
  const baseline = 28 + plot;
  const height = baseline + plot + 28;
  const width = Math.max(left + Math.max(buckets.length, 1) * groupWidth + 16, 360);
  const yFor = (value) => {
    const size = (Math.abs(value) / max) * (plot - 8);
    return value >= 0 ? baseline - size : baseline + size;
  };
  const gridSteps = [-1, -0.5, 0, 0.5, 1];
  return (
    <div style={{ marginTop: '0.25rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
        {CHART_SERIES.map((item) => (
          <label key={item.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={seriesOn[item.key]} onChange={() => onToggleSeries(item.key)} />
            <span style={{ width: 10, height: 10, backgroundColor: item.color, display: 'inline-block' }} />
            {item.label}
          </label>
        ))}
        <span style={{ display: 'inline-flex', gap: 4 }}>
          {['bar', 'line'].map((type) => (
            <button key={type} type="button" onClick={() => onChartType(type)} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #0f766e', cursor: 'pointer', backgroundColor: chartType === type ? '#0f766e' : 'white', color: chartType === type ? 'white' : '#0f766e', textTransform: 'capitalize' }}>
              {type === 'bar' ? 'Bars' : 'Line'}
            </button>
          ))}
        </span>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showLabels} onChange={(event) => onLabels(event.target.checked)} />
          Data labels
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showGrid} onChange={(event) => onGrid(event.target.checked)} />
          Grid
        </label>
      </div>
      {!buckets.length || !active.length ? <p>Select a series to draw the chart.</p> : (
        <div style={{ overflowX: 'auto' }}>
          <svg width={width} height={height} role="img" aria-label="Finance summary chart">
            {showGrid && gridSteps.map((step) => {
              const y = baseline - step * (plot - 8);
              return (
                <g key={step}>
                  <line x1={left} y1={y} x2={width - 8} y2={y} stroke={step === 0 ? '#ced4da' : '#e9ecef'} />
                  <text x={left - 6} y={y + 3} fontSize="9" textAnchor="end" fill="#6c757d">{shortAmount(step * max)}</text>
                </g>
              );
            })}
            {!showGrid && <line x1={left} y1={baseline} x2={width - 8} y2={baseline} stroke="#ced4da" />}
            {chartType === 'line' && active.map((item) => {
              const points = buckets.map((bucket, index) => {
                const value = Number(bucket[item.key]) || 0;
                const x = left + index * groupWidth + groupWidth / 2;
                return `${x},${yFor(value)}`;
              }).join(' ');
              return <polyline key={item.key} points={points} fill="none" stroke={item.color} strokeWidth="2" />;
            })}
            {buckets.map((bucket, index) => {
              const origin = left + index * groupWidth + (chartType === 'line' ? groupWidth / 2 : 8);
              return (
                <g key={bucket.key}>
                  {showGrid && <line x1={origin} y1={baseline - (plot - 8)} x2={origin} y2={baseline + (plot - 8)} stroke="#e9ecef" />}
                  {active.map((item, seriesIndex) => {
                    const value = Number(bucket[item.key]) || 0;
                    const y = yFor(value);
                    if (chartType === 'line') {
                      return (
                        <g key={item.key}>
                          <circle cx={origin} cy={y} r="3.5" fill={value < 0 ? '#dc3545' : item.color}>
                            <title>{`${item.label}: ${value}`}</title>
                          </circle>
                          {showLabels && value !== 0 && <text x={origin} y={value >= 0 ? y - 6 : y + 12} fontSize="9" textAnchor="middle" fill="#343a40">{shortAmount(value)}</text>}
                        </g>
                      );
                    }
                    const barHeight = Math.abs(baseline - y);
                    const barY = value >= 0 ? y : baseline;
                    const x = origin + seriesIndex * barWidth;
                    return (
                      <g key={item.key}>
                        <rect x={x} y={barY} width={barWidth - 2} height={barHeight} fill={value < 0 ? '#dc3545' : item.color}>
                          <title>{`${item.label}: ${value}`}</title>
                        </rect>
                        {showLabels && value !== 0 && <text x={x + 3} y={value >= 0 ? barY - 4 : barY + barHeight + 10} fontSize="9" textAnchor="middle" fill="#343a40">{shortAmount(value)}</text>}
                      </g>
                    );
                  })}
                  <text x={chartType === 'line' ? origin : origin + (active.length * barWidth) / 2} y={height - 8} fontSize="10" textAnchor="middle" fill="#495057">{chartLabel(bucket, grain)}</text>
                </g>
              );
            })}
          </svg>
        </div>
      )}
    </div>
  );
}

function SimpleTable({ headers, rows }) {
  if (!rows.length) return <p>No records for this period.</p>;
  return (
    <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {headers.map((header) => <th key={header} style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', backgroundColor: '#f8f9fa' }}>{header}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {row.map((cell, cellIndex) => <td key={cellIndex} style={{ padding: '8px', border: '1px solid #ddd' }}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default FinanceDashboard;
